import React from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
    Snackbar
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import CodeIcon from '@material-ui/icons/Code';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import LaunchIcon from '@material-ui/icons/Launch';
import { WizardButton } from './WizardButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import documentLoader from '../../utils/document-loader';
import Modal from './Modal';

export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <WizardButton
        variant="outlined"
        onClick={onClick}
        endIcon={<AddIcon style={{ fontSize: 16 }} />}
        style={{ fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>
        {label}
    </WizardButton>
);

export const DeleteBtn: React.FC<{ onClick: () => void; disabled?: boolean }> = ({
    onClick,
    disabled = false
}) => (
    <Tooltip title="Remove item" placement="top">
        <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            style={{ color: disabled ? 'transparent' : '#c8cdd6', flexShrink: 0 }}>
            <DeleteOutlineIcon style={{ fontSize: 18 }} />
        </IconButton>
    </Tooltip>
);

export const LinkOpenBtn: React.FC<{ url: string; tooltip?: string }> = ({
    url,
    tooltip = 'Open in new tab'
}) => (
    <Tooltip title={tooltip} placement="top">
        <IconButton
            size="small"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            component="a"
            style={{ color: '#c8cdd6', flexShrink: 0, cursor: 'pointer' }}>
            <LaunchIcon style={{ fontSize: 18 }} />
        </IconButton>
    </Tooltip>
);

export const DIDDocBtn: React.FC<{ did: string }> = ({ did }) => {
    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [doc, setDoc] = React.useState<object | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const handleClick = async () => {
        setLoading(true);
        try {
            const { document } = await documentLoader(did);
            setDoc(document);
            setOpen(true);
        } catch (err) {
            setError('Failed to load DID document');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Tooltip title="View DID Document" placement="top">
                <IconButton
                    size="small"
                    onClick={handleClick}
                    disabled={loading}
                    style={{ color: '#c8cdd6', flexShrink: 0, cursor: 'pointer' }}>
                    {loading ? (
                        <CircularProgress size={14} style={{ color: '#2ec4b6' }} />
                    ) : (
                        <VisibilityIcon style={{ fontSize: 18 }} />
                    )}
                </IconButton>
            </Tooltip>
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="DID Document"
                did={did}
                doc={doc}
            />
            <Snackbar
                open={!!error}
                onClose={() => setError(null)}
                message={error}
                autoHideDuration={4000}
            />
        </>
    );
};

interface OutputViewToggleProps {
    tab: number;
    onTabChange: (tab: number) => void;
    onCopy?: () => void;
    showCopy?: boolean;
    options?: {
        label: string;
        icon?: React.ReactNode;
    }[];
}

export const OutputViewToggle: React.FC<OutputViewToggleProps> = ({
    tab,
    onTabChange,
    onCopy,
    showCopy = false,
    options = [
        {
            label: 'Encoded',
            icon: <LockOpenIcon style={{ fontSize: 14 }} />
        },
        {
            label: 'JSON',
            icon: <CodeIcon style={{ fontSize: 14 }} />
        }
    ]
}) => (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <Box
            display="flex"
            alignItems="center"
            style={{
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
                padding: 3,
                border: '1px solid #e8e8e8'
            }}>
            {options.map((opt, idx) => {
                const selected = tab === idx;

                return (
                    <Box
                        key={idx}
                        onClick={() => onTabChange(idx)}
                        padding={{
                            xs: opt.icon ? '0px 7px' : '0',
                            lg: opt.icon ? '0px 14px' : '0'
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            width: opt.icon ? '80px' : '60px',
                            height: '30px',
                            borderRadius: 6,
                            cursor: 'pointer',
                            backgroundColor: selected ? '#fff' : 'transparent',
                            boxShadow: selected ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                            border: selected ? '1px solid #e0e0e0' : '1px solid transparent',
                            transition: 'all 0.15s ease',
                            textAlign: 'center'
                        }}>
                        {opt.icon && (
                            <Box style={{ color: selected ? '#2ec4b6' : '#999' }}>{opt.icon}</Box>
                        )}

                        <Typography
                            style={{
                                fontSize: '0.82rem',
                                fontWeight: 400,
                                color: selected ? '#2ec4b6' : '#999',
                                whiteSpace: 'nowrap'
                            }}>
                            {opt.label}
                        </Typography>
                    </Box>
                );
            })}
        </Box>

        {showCopy && onCopy && (
            <IconButton
                size="small"
                onClick={onCopy}
                style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 6,
                    padding: 6,
                    backgroundColor: '#fff'
                }}>
                <FileCopyOutlinedIcon style={{ fontSize: 16, color: '#555' }} />
            </IconButton>
        )}
    </Box>
);
